/**
 * R10e36.71.1-F773: Sudo-Mode Re-Authentication Modal
 * 
 * Intercepts sensitive actions that require re-authentication.
 * Captures user password, verifies via /auth/sudo-verify endpoint,
 * and on success allows the original action to proceed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import apiClient from '../api';
import { toast } from 'sonner';

// Sudo mode state management
let sudoVerifiedAt = null;
const SUDO_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const isSudoActive = () => {
  if (!sudoVerifiedAt) return false;
  return (Date.now() - sudoVerifiedAt) < SUDO_DURATION_MS;
};

export const setSudoVerified = () => {
  sudoVerifiedAt = Date.now();
};

export const clearSudo = () => {
  sudoVerifiedAt = null;
};

// Global callback to trigger modal from interceptor
let showSudoModalCallback = null;
let pendingRequest = null;

export const registerSudoModal = (showCallback) => {
  showSudoModalCallback = showCallback;
};

export const triggerSudoModal = (failedRequest) => {
  pendingRequest = failedRequest;
  if (showSudoModalCallback) {
    showSudoModalCallback(true);
  }
};

export const getPendingRequest = () => pendingRequest;
export const clearPendingRequest = () => { pendingRequest = null; };

const SudoModal = ({ open, onOpenChange, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setPassword('');
      setError('');
      setShowPassword(false);
    }
  }, [open]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      await apiClient.post('/auth/sudo-verify', { password });
      setSudoVerified();
      toast.success('Identity verified');
      onOpenChange(false);
      
      // R10e36.71.2.6-F814-c: Execute the pending retry (from api.js interceptor)
      const pendingRetry = getPendingRequest();
      if (pendingRetry) {
        clearPendingRequest();
        try {
          await pendingRetry();
          toast.success('Action completed');
        } catch (retryErr) {
          // Retry failed for a reason other than sudo — show its error
          const rd = retryErr.response?.data?.detail;
          if (rd) toast.error(rd);
        }
      }
      
      onSuccess?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      setError(detail);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sudo-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <DialogTitle>Confirm Your Identity</DialogTitle>
              <DialogDescription>
                This action requires re-authentication for security.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleVerify} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sudo-password">Enter your password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="sudo-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="pl-10 pr-10"
                autoFocus
                data-testid="sudo-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <p className="text-xs text-slate-500">
            You&apos;ll stay verified for 5 minutes after successful authentication.
          </p>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={verifying}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={verifying || !password.trim()}
              data-testid="sudo-verify-btn"
            >
              {verifying ? 'Verifying...' : 'Verify Identity'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Hook to use sudo modal
export const useSudoModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [onSuccessCallback, setOnSuccessCallback] = useState(null);

  useEffect(() => {
    registerSudoModal(setShowModal);
  }, []);

  const requireSudo = useCallback((action) => {
    if (isSudoActive()) {
      action();
      return;
    }
    setOnSuccessCallback(() => action);
    setShowModal(true);
  }, []);

  const handleSuccess = useCallback(() => {
    if (onSuccessCallback) {
      onSuccessCallback();
      setOnSuccessCallback(null);
    }
  }, [onSuccessCallback]);

  return {
    showModal,
    setShowModal,
    requireSudo,
    handleSuccess,
  };
};

export default SudoModal;
